import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import "./ShapesGame.css";
import { AudioManager } from "../audio/AudioManager";
import { MusicController } from "../audio/MusicController";

type ShapeId =
  | "triangle"
  | "square"
  | "rectangle"
  | "circle"
  | "pentagon"
  | "hexagon"
  | "octagon"
  | "trapezoid"
  | "rhombus"
  | "ellipse";

interface ShapeMeta {
  id: ShapeId;
  label: string;
  render: () => ReactElement;
}

const SHAPES: ShapeMeta[] = [
  {
    id: "triangle",
    label: "Trójkąt",
    render: () => (
      <polygon points="50,12 92,88 8,88" className="ShapeFill" />
    ),
  },
  {
    id: "square",
    label: "Kwadrat",
    render: () => <rect x="18" y="18" width="64" height="64" className="ShapeFill" />,
  },
  {
    id: "rectangle",
    label: "Prostokąt",
    render: () => <rect x="10" y="28" width="80" height="44" className="ShapeFill" />,
  },
  {
    id: "circle",
    label: "Koło",
    render: () => <circle cx="50" cy="50" r="34" className="ShapeFill" />,
  },
  {
    id: "pentagon",
    label: "Pięciokąt",
    render: () => (
      <polygon points="50,10 90,38 74,88 26,88 10,38" className="ShapeFill" />
    ),
  },
  {
    id: "hexagon",
    label: "Sześciokąt",
    render: () => (
      <polygon points="50,10 86,30 86,70 50,90 14,70 14,30" className="ShapeFill" />
    ),
  },
  {
    id: "octagon",
    label: "Ośmiokąt",
    render: () => (
      <polygon points="30,10 70,10 90,30 90,70 70,90 30,90 10,70 10,30" className="ShapeFill" />
    ),
  },
  {
    id: "trapezoid",
    label: "Trapez",
    render: () => (
      <polygon points="22,18 78,18 90,82 10,82" className="ShapeFill" />
    ),
  },
  {
    id: "rhombus",
    label: "Romb",
    render: () => (
      <polygon points="50,8 88,50 50,92 12,50" className="ShapeFill" />
    ),
  },
  {
    id: "ellipse",
    label: "Elipsa",
    render: () => <ellipse cx="50" cy="50" rx="38" ry="26" className="ShapeFill" />,
  },
];

const randomPick = <T,>(items: T[]): T => {
  return items[Math.floor(Math.random() * items.length)];
};

const buildChoices = (correct: ShapeMeta): ShapeMeta[] => {
  const choices = new Set<ShapeId>([correct.id]);
  while (choices.size < 3) {
    choices.add(randomPick(SHAPES).id);
  }
  return Array.from(choices)
    .map((id) => SHAPES.find((shape) => shape.id === id)!)
    .sort(() => Math.random() - 0.5);
};

interface ShapesGameProps {
  onBack: () => void;
}

export function ShapesGame({ onBack }: ShapesGameProps) {
  const audioRef = useRef<AudioManager | null>(null);
  const musicRef = useRef<MusicController | null>(null);
  const musicPrimedRef = useRef(false);
  if (!audioRef.current) {
    audioRef.current = new AudioManager({
      successUrl: `${process.env.PUBLIC_URL}/music/success.wav`,
      failUrl: `${process.env.PUBLIC_URL}/music/fail.wav`,
      successVolume: 0.8,
      failVolume: 0.8,
    });
  }
  if (!musicRef.current) {
    musicRef.current = new MusicController({
      playlist: [
        `${process.env.PUBLIC_URL}/music/bg_000.mp3`,
        `${process.env.PUBLIC_URL}/music/bg_001.mp3`,
      ],
      volume: 0.4,
    });
  }

  const [shape, setShape] = useState<ShapeMeta>(() => randomPick(SHAPES));
  const [locked, setLocked] = useState(false);
  const [lastResult, setLastResult] = useState<"correct" | "incorrect" | null>(
    null
  );
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const timerRef = useRef<number | null>(null);

  const choices = useMemo(() => buildChoices(shape), [shape]);

  const primeMusic = () => {
    if (musicPrimedRef.current) {
      return;
    }
    musicPrimedRef.current = true;
    musicRef.current?.start();
  };

  const nextShape = () => {
    setShape(randomPick(SHAPES));
    setLocked(false);
    setLastResult(null);
  };

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        musicRef.current?.pause();
        return;
      }
      if (musicPrimedRef.current) {
        musicRef.current?.resume();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      musicRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    if (!lastResult) {
      return;
    }
    timerRef.current = window.setTimeout(() => {
      nextShape();
    }, 2000);
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [lastResult]);

  const handleChoice = (choice: ShapeMeta) => {
    if (locked) {
      return;
    }
    primeMusic();
    const correct = choice.id === shape.id;
    setLocked(true);
    setLastResult(correct ? "correct" : "incorrect");
    setScore((prev) => ({
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1,
    }));
    if (correct) {
      audioRef.current?.playSuccess();
    } else {
      audioRef.current?.playFail();
    }
  };

  return (
    <div className="ShapesGame">
      <div className="ShapesCard">
        <div className="ShapesHeader">
          <div>
            <div className="ShapesTitle">Kształty</div>
            <div className="ShapesSubtitle">Wybierz nazwę figury</div>
          </div>
          <button className="ShapesBack" type="button" onClick={onBack}>
            Menu
          </button>
        </div>
        <div className="ShapesCanvas" onClick={primeMusic} role="presentation">
          <svg viewBox="0 0 100 100" className="ShapesSvg">
            {shape.render()}
          </svg>
        </div>
        <div className="ShapesChoices">
          {choices.map((choice) => {
            const isCorrect = locked && choice.id === shape.id;
            const isIncorrect = locked && choice.id !== shape.id;
            return (
              <button
                key={choice.id}
                className={`ShapesChoice ${
                  isCorrect ? "is-correct" : ""
                } ${isIncorrect && lastResult === "incorrect" ? "is-incorrect" : ""}`}
                type="button"
                disabled={locked}
                onClick={() => handleChoice(choice)}
              >
                {choice.label}
              </button>
            );
          })}
        </div>
        <div className="ShapesScore">
          Wynik: {score.correct}/{score.total}
        </div>
      </div>
    </div>
  );
}
